import { useEffect, useState } from 'react';

import { IconExternalLink } from '@tabler/icons-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  Heading,
  LoadingIndicator,
  Modal,
  Text,
} from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';

import { itemByLanguage } from '@/utils/language';

import Constants from '@/constants';

import { closePrintReportAtom, printReportAtom, setShowDoBAtom } from './state';
import { usePrintReportData } from './usePrintReportData';

/**
 * Unified PrintReport component that handles printing for:
 * - Plan reports (compliance and non-compliance)
 * - Team member summary reports
 *
 * Reads from printReportAtom for what to print.
 * Mount this once at the app level (Layout.tsx).
 */
const PrintReport: React.FC = () => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();

  const isAuthLoaded = !!auth?.userId;
  const locationsWithAtLeastLeaderPermission =
    auth?.allLocationsWithAtLeastLeaderPermissions ?? [];

  const { subject, showDoB } = useAtomValue(printReportAtom);
  const setShowDoB = useSetAtom(setShowDoBAtom);
  const closePrintReport = useSetAtom(closePrintReportAtom);

  const isOpen = subject !== null;
  const isPlanPrint = subject?.type === 'plan';
  const isTeamMemberPrint = subject?.type === 'teamMember';
  const isCompliance = isPlanPrint && subject.plan.type === 'COMPLIANCE';

  // Modal state
  const [printStep, setPrintStep] = useState(0);
  const [noLocationsSelected, setNoLocationsSelected] = useState(false);
  const [isLoadedWithMinTime, setIsLoadedWithMinTime] = useState(false);
  const [userSelectedLocations, setUserSelectedLocations] = useState<
    string[] | null
  >(null);

  // Fetch report data (lazy - call startFetching to trigger)
  const { printLocations, isFetching, generateReport, startFetching } =
    usePrintReportData({ subject });

  // Final selected locations (user selection or defaults)
  const defaultLocations = isPlanPrint
    ? isCompliance
      ? locationsWithAtLeastLeaderPermission
      : printLocations
    : locationsWithAtLeastLeaderPermission;
  const selectedLocations = userSelectedLocations ?? defaultLocations;
  const locationOptions = isPlanPrint
    ? printLocations
    : isTeamMemberPrint
      ? []
      : locationsWithAtLeastLeaderPermission;

  const isFoodSafety =
    isPlanPrint &&
    itemByLanguage(subject.plan.name)?.includes(
      Constants.FOOD_SAFETY_COURSE_NAME,
    );

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    setPrintStep(0);
    setNoLocationsSelected(false);
    setIsLoadedWithMinTime(false);
    setUserSelectedLocations(null);
  }

  const effectivePrintStep =
    printStep === 1 && isLoadedWithMinTime && !isFetching ? 2 : printStep;

  // Minimum loading time to avoid flash
  useEffect(() => {
    if (printStep === 1) {
      const timer = setTimeout(() => setIsLoadedWithMinTime(true), 1200);
      return () => clearTimeout(timer);
    }
  }, [printStep]);

  const handleClose = () => {
    closePrintReport();
  };

  const handleCreatePdf = () => {
    if (!selectedLocations.length && locationOptions.length) {
      setNoLocationsSelected(true);
    } else {
      startFetching(selectedLocations);
      setPrintStep(1);
    }
  };

  const handlePrint = () => {
    generateReport(showDoB, selectedLocations);
    handleClose();
  };

  if (!isAuthLoaded || !isOpen) {
    return null;
  }

  return (
    <Modal.Overlay
      className="z-2000!"
      data-testid="PrintReportModal"
      onOpenChange={handleClose}
      isOpen={isOpen}
      isDismissable
    >
      <Modal.Dialog size="sm">
        {effectivePrintStep === 0 && (
          <>
            <Modal.Title
              data-testid="PrintReportModalHeaderText"
              className="text-grayscale-gray6! text-center! text-2xl!"
            >
              {t('Generic.printReport')}
            </Modal.Title>
            <Modal.Content className="items-stretch! [--dialog-padding:20px_32px_24px]!">
              <Text className="text-grayscale-gray6! text-center">
                {t('TrainingPlans.chooseWhatToInclude')}
              </Text>

              <Divider className="my-2!" />

              {locationOptions.length ? (
                <CheckboxGroup
                  className="text-left! [&>span]:text-base!"
                  label={t('Generic.locations')}
                  value={selectedLocations}
                  onChange={setUserSelectedLocations}
                  isInvalid={noLocationsSelected && !selectedLocations.length}
                  errorMessage={t('Generic.selectAtLeastOne')}
                >
                  {locationOptions.map(location => (
                    <Checkbox
                      key={location}
                      value={location}
                      className="text-base! font-normal! [&>label]:flex! [&>label]:items-center!"
                    >
                      {location}
                    </Checkbox>
                  ))}
                </CheckboxGroup>
              ) : (
                <Text className="text-grayscale-gray6! text-center italic!">
                  {t('TrainingPlans.noOptionsAvailable')}
                </Text>
              )}

              {isFoodSafety && (
                <>
                  <Heading
                    level={3}
                    className="text-grayscale-gray6! bold! mt-4! text-left! font-bold!"
                  >
                    {t('Generic.options')}
                  </Heading>
                  <Checkbox
                    isSelected={showDoB}
                    onChange={setShowDoB}
                    className={
                      'text-base! font-normal! [&>label]:flex! [&>label]:items-center!'
                    }
                  >
                    {t('Generic.showDoB')}
                  </Checkbox>
                </>
              )}

              <Divider className="my-2!" />
            </Modal.Content>
            <Modal.Footer>
              <Button
                color="secondary"
                onPress={handleClose}
                variant="outlined"
                className="min-w-40!"
              >
                {t('Button.cancel')}
              </Button>
              <Button
                color="secondary"
                onPress={handleCreatePdf}
                variant="filled"
                className="min-w-40!"
              >
                {t('Button.createPdf')}
              </Button>
            </Modal.Footer>
          </>
        )}

        {effectivePrintStep === 1 && (
          <Modal.Content>
            <LoadingIndicator size="md" />
            <Text className="text-grayscale-gray7!">
              {t('Generic.creatingPdf')}
            </Text>
          </Modal.Content>
        )}

        {effectivePrintStep === 2 && (
          <Modal.Content>
            <Heading
              level={2}
              variant="headline4"
              className="text-grayscale-gray6!"
            >
              {t('Generic.reportReady')}
            </Heading>
            <Text className="text-grayscale-gray6! pb-4!">
              {t('Generic.saveOrPrint')}
            </Text>
            <Button color="secondary" onPress={handlePrint} variant="filled">
              <IconExternalLink className="mr-2 size-5" />
              {t('Button.viewAndPrint')}
            </Button>
          </Modal.Content>
        )}
      </Modal.Dialog>
    </Modal.Overlay>
  );
};

export default PrintReport;
